import { varAlpha } from 'minimal-shared/utils';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';

import { RouterLink } from 'src/routes/components';

import { trackPublicCta } from 'src/sections/common/track-public-cta';
import { usePublicTrialHref } from 'src/sections/common/hooks/model/use-public-trial-href';

import { useHomeStickyCta } from '../hooks/model/use-home-sticky-cta';
import { CM, PUBLIC_CTA_SX, PUBLIC_FRAME_SHADOW } from '../constants/home-theme';

// ----------------------------------------------------------------------

/** 고정 단추가 차지하는 높이(px) — 폰에서 홈 바닥에 이만큼 비운다(흐림 위쪽은 비치므로 단추 몫만) */
export const HOME_STICKY_CTA_ROOM = 76;

// ----------------------------------------------------------------------

/**
 * 폰 아래 고정 「무료로 시작하기」 — 장면을 내려 보다가 바로 누를 수 있게 엄지 자리에 둔다.
 *
 * 🔑 폰(sm 아래)에서만 선다 · 첫 화면 단추가 보이는 동안과 맨 아래 체험 구역이 보일 때는 내려가 있다(`useHomeStickyCta`).
 * 🔑 가는 곳은 첫 화면 단추와 같다(`usePublicTrialHref` — 비회원은 로그인 화면) — 기록 이름만 다르다(`home.sticky.trial`).
 * ⚠️ 홈 바닥에 이 띠 높이만큼 여백이 있어야 마지막 줄이 안 가린다 — 조립부가 준다(`HOME_STICKY_CTA_ROOM`).
 */
export function HomeStickyCta() {
  const visible = useHomeStickyCta();
  const trialHref = usePublicTrialHref();
  const label = '무료로 시작하기';

  return (
    <Box
      aria-hidden={!visible}
      sx={(theme) => ({
        display: { xs: 'block', sm: 'none' },
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: theme.zIndex.appBar,
        px: 2,
        pt: 4,
        pb: 'calc(12px + env(safe-area-inset-bottom))',
        // 띠가 아니라 흐림 — 아래는 홈 바탕색(흰 · `background.paper`)으로 꽉 차고 위로 갈수록 투명해진다.
        // 흐림은 터치를 막지 않는다(단추만 눌린다)
        pointerEvents: 'none',
        backgroundImage: `linear-gradient(to top, ${varAlpha(theme.vars.palette.background.paperChannel, 1)} 0%, ${varAlpha(theme.vars.palette.background.paperChannel, 0.92)} 40%, ${varAlpha(theme.vars.palette.background.paperChannel, 0)} 100%)`,
        transform: visible ? 'none' : 'translateY(110%)',
        visibility: visible ? 'visible' : 'hidden',
        transition: 'transform 0.25s ease, visibility 0.25s',
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      })}
    >
      <Button
        fullWidth
        component={RouterLink}
        href={trialHref}
        variant="contained"
        tabIndex={visible ? 0 : -1}
        onClick={() => trackPublicCta('home.sticky.trial', label)}
        sx={{ ...PUBLIC_CTA_SX, height: 48, pointerEvents: 'auto', bgcolor: CM.primary, boxShadow: PUBLIC_FRAME_SHADOW, '&:hover': { bgcolor: CM.primaryDark, boxShadow: PUBLIC_FRAME_SHADOW } }}
      >
        {label}
      </Button>
    </Box>
  );
}
